import { Box, Stack, styled } from "@mui/material";
import "./TermOfService.css";

const Container = styled(Box)`
  font-family: Arial, sans-serif;
  line-height: 1.6;
  margin: 20px;
  max-width: 1024px;
  padding: 0 40px 40px 40px;
  margin: 0 auto;
`;

export const TermOfService = () => {
  return (
    <Container className="tos">
      <Stack
        mt={3}
        mb={6}
        direction="row"
        justifyContent={"space-between"}
        spacing={2}
        alignItems="center"
      >
        <img
          loading="lazy"
          src="https://cdn.prod.website-files.com/6666a6473da3c16dcd1d55ad/6666abb2e3f8d71eea95b414_Logo.svg"
          alt="Logo"
          className="logo-image"
        />
        <Stack alignItems={"flex-end"} gap={0.25}>
          <Box className="mail-and-site">
            <strong>W:</strong>
            &nbsp;
            <a href="https://referral-stream.com">
              https://referral-stream.com
            </a>
          </Box>
          <Box className="mail-and-site">
            <strong>E:</strong>
            &nbsp;
            <a href="mailto:info@referral-stream.com">
              info@referral-stream.com
            </a>
          </Box>
        </Stack>
      </Stack>
      <h1>Terms of Service</h1>

      <h2>Introduction</h2>
      <p>
        Welcome to Referral Stream. By accessing or using our services, you
        agree to comply with and be bound by the following terms and conditions.
        Please read this Agreement carefully. If you do not agree with these
        terms, you are prohibited from using our services. Your continued use of
        the service signifies your acceptance of any changes to these terms.
      </p>

      <h2>Definitions</h2>
      <ol>
        <li>
          <strong>Referral Stream:</strong> The service provided by Referral
          Stream, which includes both physical and digital buttons, as well as
          any associated software or platform used to facilitate referrals.
        </li>
        <li>
          <strong>User:</strong> Any individual or entity using the services of
          Referral Stream, including healthcare providers, businesses, and
          authorized persons granted usage or permission by registered
          organization.
        </li>
        <li>
          <strong>Service:</strong> The referral notification system provided by
          Referral Stream, which facilitates communication between users and
          their referral recipients and involves the exchange of Protected
          Health Information (PHI).
        </li>
        <li>
          <strong>Notifications:</strong> Alerts sent via physical or digital
          buttons, email, text or in-app messaging to inform recipients of
          potential referrals or other updates.
        </li>
      </ol>

      <h2>Use of Service</h2>
      <h3>Eligibility</h3>
      <p>
        Referral Stream is available for use by authorized individuals and
        entities, including healthcare professionals and their representatives.
        By using the platform, you affirm that you and all peoples or entities
        you provide access to have the necessary authority to handle PHI and
        that you are in compliance with all applicable privacy laws and
        regulations, including HIPAA.
      </p>

      <h3>Account Security</h3>
      <p>
        Users are responsible for the security of their account credentials. You
        must maintain the confidentiality of your login information and promptly
        notify Referral Stream if you suspect unauthorized access or misuse of
        your account.
      </p>

      <h3>Prohibited Uses</h3>
      <p>
        The use of Referral Stream for unlawful purposes, including the
        unauthorized dissemination of PHI or violation of intellectual property
        rights, is strictly prohibited. Users agree not to interfere with or
        disrupt the integrity of the service or its associated systems.
      </p>

      <h2>Service Description</h2>
      <p>
        Referral Stream provides a notification system utilizing Internet of
        Things (IoT) devices, including physical buttons, as well as digital
        buttons to alert recipients of potential referrals. In addition, the
        service includes an embedded referral form for instant patient
        referrals. The platform enables seamless and secure communication
        between healthcare providers and authorized entities, ensuring the
        timely and compliant exchange of PHI as required under HIPAA.
      </p>

      <h2>Intellectual Property</h2>
      <p>
        Referral Stream retains all ownership rights to the intellectual
        property on its platform, including but not limited to software,
        content, trademarks, service marks, and logos. Users may not reproduce,
        distribute, or create derivative works from any content on the platform
        without prior written consent from Referral Stream.
      </p>

      <h2>Limitation of Liability</h2>
      <p>
        Referral Stream will not be liable for any direct, indirect, incidental,
        special, or consequential damages resulting from the use or inability to
        use the service, including, but not limited to, reliance on any
        information obtained through the service or that result from mistakes,
        omissions, interruptions, deletion of files, or any failure of
        performance. To the extent permitted by applicable law, we, our
        affiliates, officers, employees, agents, suppliers, and licensors will
        not be liable for (a) any indirect, consequential, special, incidental,
        punitive, or exemplary damages whatsoever, or (b) loss of use, data,
        business, revenues, or profits (in each case whether direct or
        indirect), arising out of or in connection with the services and these
        terms, and whether based on contract, tort, strict liability, or any
        other legal theory, even if we have been advised of the possibility of
        such damages and even if a remedy fails of its essential purpose. To the
        extent permitted by applicable law, the aggregate liability of each of
        us, our affiliates, officers, employees, agents, suppliers, and
        licensors arising out of or in connection with the services and these
        terms will not exceed the amount you paid to us for use of the services
        at issue during the 12 months prior to the event giving rise to the
        liability.
      </p>

      <h2>Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless Referral Stream and its
        affiliates, officers, agents, and employees from any claim or demand,
        including reasonable attorneys' fees, made by any third party due to or
        arising out of the user's breach of these terms, or violation of any law
        or the rights of a third party.
      </p>

      <h2>Business Associate Agreement (BAA) Policy</h2>
      <p>
        Referral Stream, as a Business Associate under the Health Insurance
        Portability and Accountability Act (HIPAA), acknowledges its
        responsibility to safeguard Protected Health Information (PHI) in
        accordance with federal regulations. As such, the following provisions
        apply:
      </p>
      <ol>
        <li>
          <strong>Compliance with HIPAA:</strong> Referral Stream agrees to
          comply with all applicable provisions of HIPAA and the Health
          Information Technology for Economic and Clinical Health (HITECH) Act,
          ensuring the confidentiality, integrity, and availability of all PHI
          received, maintained, or transmitted on behalf of its users.
        </li>
        <li>
          <strong>Permitted Uses and Disclosures:</strong> Referral Stream will
          only use or disclose PHI as permitted by this Agreement or as required
          by law. Any unauthorized use or disclosure of PHI will be promptly
          reported to the user in accordance with HIPAA regulations.
        </li>
        <li>
          <strong>Safeguards:</strong> Referral Stream implements appropriate
          administrative, physical, and technical safeguards to protect the
          privacy and security of PHI, ensuring that all workforce members are
          trained on HIPAA compliance.
        </li>
        <li>
          <strong>Subcontractors:</strong> Referral Stream will ensure that any
          subcontractor or agent to whom it provides PHI agrees to the same
          restrictions, conditions, and requirements that apply to Referral
          Stream under this Agreement.
        </li>
        <li>
          <strong>Breach Notification:</strong> Referral Stream agrees to
          promptly notify users of any breach of unsecured PHI, as defined under
          HIPAA, in accordance with the breach notification rules.
        </li>
        <li>
          <strong>Return or Destruction of PHI:</strong> Upon termination of
          this Agreement, Referral Stream will return or destroy all PHI
          received from or created on behalf of the user, where feasible. If
          return or destruction is not feasible, Referral Stream will continue
          to protect the PHI in accordance with this Agreement and applicable
          law.
        </li>
      </ol>
      <p>
        By using Referral Stream, the user acknowledges that a Business
        Associate Agreement is in effect, ensuring compliance with HIPAA and
        protecting all PHI managed or transmitted via Referral Stream.
      </p>

      <h2>Data Usage and Privacy Policy</h2>
      <p>
        Referral Stream is committed to protecting your privacy. The following
        explains how we collect, use, and safeguard your data:
      </p>
      <h3>Information We Collect:</h3>
      <ol>
        <li>
          <strong>Personal Information:</strong> We collect personal
          information, such as names, email addresses, phone numbers, and other
          details that you provide when registering for our services.
        </li>
        <li>
          <strong>Protected Health Information (PHI):</strong> PHI may be
          collected, stored, and transmitted through the platform in compliance
          with applicable laws, including HIPAA.
        </li>
        <li>
          <strong>Non-Personal Information:</strong> We collect non-personal
          information, including device information, IP addresses, and usage
          data, to enhance the performance and functionality of our platform.
        </li>
      </ol>
      <h3>How We Use Your Information:</h3>
      <ol>
        <li>
          <strong>Service Provision:</strong> We use personal and PHI
          information to provide and improve our services.
        </li>
        <li>
          <strong>Communication:</strong> We may contact Users regarding service
          updates, product developments or issues related to their accounts.
        </li>
        <li>
          <strong>Analytics:</strong> Non-personal data may be used for
          analytical purposes to optimize and improve the functionality of our
          services.
        </li>
      </ol>
      <h3>User Rights:</h3>
      <ol>
        <li>
          <strong>Access:</strong> You have the right to request access to the
          personal data that we hold about you.
        </li>
        <li>
          <strong>Correction:</strong> You have the right to request corrections
          to any inaccurate or incomplete personal data.
        </li>
        <li>
          <strong>Deletion:</strong> You have the right to request the deletion
          of your personal data, subject to certain legal obligations or
          exceptions.
        </li>
        <li>
          <strong>Objection:</strong> You have the right to object to the
          processing of your personal data in specific situations, such as for
          marketing purposes.
        </li>
        <li>
          <strong>Restriction:</strong> You have the right to request the
          restriction of how we process your data in certain circumstances.
        </li>
        <li>
          <strong>Data Portability:</strong> Where applicable, you have the
          right to request that your personal data be transferred to another
          service provider in a structured, commonly used, and machine-readable
          format.
        </li>
      </ol>
      <p>
        <strong>Third-Party Sharing:</strong> No mobile information will be
        shared with third parties or affiliates for marketing or promotional
        purposes. All the above categories exclude text messaging originator
        opt-in data and consent; this information will not be shared with any
        third parties.
      </p>
      <p>
        To exercise any of these rights, please contact us at{" "}
        <a href="mailto:support@referral-stream.com">
          support@referral-stream.com
        </a>
        .
      </p>

      <h2>Security</h2>
      <p>
        Referral Stream employs industry-standard security protocols, including
        encryption and secure access controls, to safeguard personal and PHI
        data. While we take all reasonable precautions to protect your
        information, no system can guarantee absolute security. Users
        acknowledge that they are using the service at their own risk.
      </p>

      <h2>Data Retention</h2>
      <p>
        We retain your personal information and PHI only as long as necessary to
        provide the services outlined in this Agreement or as required by law.
      </p>

      <h2>Governing Law</h2>
      <p>
        This Agreement is governed by and constructed in accordance with the
        laws of the State of Texas and the specific laws of Harris County,
        without regard to its conflict of law principles. Any disputes arising
        out of or in connection with this Agreement shall be resolved under the
        applicable laws of Harris County, Texas.
      </p>

      <h2>General Terms, Disclaimers, & Use</h2>
      <p>
        Referral Stream provides all marketing content for informational and
        educational purposes only. Any and all numbers displayed, calculators
        provided, and estimates are simply examples and informative and should
        NOT be relied upon and are NOT guarantees of any performance metrics,
        results you should expect, or any other promises and should NOT in any
        way be relied upon for buying decisions. We do not offer financial,
        investment, or legal advice, and any information provided should not be
        construed as such. Users are encouraged to seek independent professional
        advice and conduct their research before making any financial,
        marketing, or legal decisions. Referral Stream shall not be held
        responsible for any actions taken or decisions made based on the
        information provided on our platform.
      </p>

      <h2>Amendments</h2>
      <p>
        Referral Stream reserves the right to amend or modify these terms at any
        time. Users will be notified of such amendments, and continued use of
        the service constitutes acceptance of the updated terms.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions or concerns about this Agreement or our
        services, please contact us at{" "}
        <a href="mailto:support@referral-stream.com">
          support@referral-stream.com
        </a>
        .
      </p>
    </Container>
  );
};
